import React, { useEffect, useRef, useState } from "react";
import { WaveSVG } from "./WaveSvg";
import "../styles/projects.scss";
import { projects } from "../projects";

const useScrollAnimation = (scrollDistance?: number) => {
  const elementRef = useRef<HTMLElement | null>(null);
  const [offset, setOffset] = useState(0);
  const [startOffset, setStartOffset] = useState(
    window.innerWidth <= 768 ? 100 : 200 // Set 100 for mobile, 200 for desktop
  );
  const [activeScrollDistance, setActiveScrollDistance] = useState(
    scrollDistance || (window.innerWidth <= 768 ? 300 : 1000)
  );

  useEffect(() => {
    const updateStartOffset = () => {
      setStartOffset(window.innerWidth <= 768 ? 100 : 300);
      setActiveScrollDistance(
        scrollDistance || (window.innerWidth <= 768 ? 300 : 1000)
      );
    };

    window.addEventListener("resize", updateStartOffset);
    updateStartOffset();

    return () => window.removeEventListener("resize", updateStartOffset);
  }, [scrollDistance]);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const handleScroll = () => {
      const rect = element.getBoundingClientRect();
      const elementTop = rect.top + window.scrollY;
      const scrollStart = elementTop - window.innerHeight;
      const scrollPos = window.scrollY;
      const scrollPercentage = Math.max(
        0,
        Math.min((scrollPos - scrollStart) / activeScrollDistance, 1)
      );

      const eased = easeOutCubic(scrollPercentage);
      const newOffset = startOffset - startOffset * eased;
      setOffset(newOffset);
    };

    const easeOutCubic = (x: number) => 1 - Math.pow(1 - x, 3);

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [startOffset, activeScrollDistance]);

  return [elementRef, offset];
};

interface AnimatedImageProps {
  src: string;
  alt?: string;
  className?: string;
}

const AnimatedImage: React.FC<AnimatedImageProps> = ({
  src,
  alt = "",
  className = "",
}) => {
  const [ref, offset] = useScrollAnimation();

  return (
    <img
      ref={ref as React.MutableRefObject<HTMLImageElement>}
      src={src}
      alt={alt}
      className={className}
      style={{
        transform: `translateY(${offset}px)`,
        transition: "transform 0.2s ease-out",
        willChange: "transform", // Optimize performance
      }}
    />
  );
};

export default AnimatedImage;

export const Projects = () => {
  return (
    <div className="projects-container">
      <div className="projects">
        <div className="container">
          <div className="content">
            <h1>my projects:</h1>
            {projects.map((project, index) => {
              const ProjectWrapper = project.link ? "a" : "div";
              const wrapperProps = project.link
                ? {
                    href: project.link,
                    target: "_blank",
                    rel: "noopener noreferrer",
                  }
                : {};

              return (
                <ProjectWrapper
                  key={index}
                  className="project"
                  {...wrapperProps}
                >
                  <div className="project-svg-container">
                    <AnimatedImage src={project.image} alt={project.imageAlt} />
                  </div>
                  <div className="text">
                    <h2>{project.title}</h2>
                    <p>{project.description}</p>
                    <div className="date">
                      <p>{project.date}</p>
                      {project.technologies.map((tech, techIndex) => (
                        <img key={techIndex} src={tech} alt="" />
                      ))}
                    </div>
                  </div>
                </ProjectWrapper>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
